import React, { useState, useEffect } from 'react';
import { Play } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/VideoSection.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Übersetzungen für die Video-Sektion
const videoSectionTranslations = {
  de: {
    title: 'Entdecke unsere Traumziele',
    subtitle: 'Lass dich von diesen wunderschönen Hochzeitslocations inspirieren'
  },
  en: {
    title: 'Discover Our Dream Destinations',
    subtitle: 'Get inspired by these beautiful wedding locations'
  },
  es: {
    title: 'Descubre Nuestros Destinos de Ensueño',
    subtitle: 'Déjate inspirar por estas hermosas ubicaciones de boda'
  }
};

const VideoSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const { language } = useLanguage();
  
  // Sektionstexte basierend auf Sprache
  const sectionText = videoSectionTranslations[language] || videoSectionTranslations.de;

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Fehler beim Abrufen der Videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVideoTitle = (video) => {
    if (language === 'en' && video.title_en) return video.title_en;
    if (language === 'es' && video.title_es) return video.title_es;
    return video.title;
  };

  const getVideoDescription = (video) => {
    if (language === 'en' && video.description_en) return video.description_en;
    if (language === 'es' && video.description_es) return video.description_es;
    return video.description;
  };

  const handleVideoClick = async (videoId) => {
    try {
      await axios.post(`${API}/videos/${videoId}/click`);
      setPlayingVideo(videoId);
    } catch (error) {
      console.error('Fehler beim Verfolgen des Video-Klicks:', error);
    }
  };

  const isCloudinaryUrl = (url) => {
    return url && url.includes('cloudinary.com');
  };

  const isYoutubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  const extractYoutubeId = (url) => {
    const embedMatch = url.match(/\/embed\/([a-zA-Z0-9_-]+)/);
    if (embedMatch) return embedMatch[1];
    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
    if (watchMatch) return watchMatch[1];
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (shortMatch) return shortMatch[1];
    const idMatch = url.match(/([a-zA-Z0-9_-]{11})/);
    return idMatch ? idMatch[1] : '';
  };

  const getThumbnailUrl = (video) => {
    if (isCloudinaryUrl(video.url)) {
      return video.thumbnail_url || video.url.replace(/\.[^/.]+$/, '.jpg');
    } else if (isYoutubeUrl(video.url)) {
      return `https://img.youtube.com/vi/${extractYoutubeId(video.url)}/maxresdefault.jpg`;
    }
    return video.thumbnail_url || null;
  };

  const renderVideoPlayer = (video) => {
    if (isCloudinaryUrl(video.url)) {
      return (
        <video
          src={video.url}
          className="video-player"
          controls
          autoPlay
          playsInline
        />
      );
    } else if (isYoutubeUrl(video.url)) {
      return (
        <iframe
          src={`${video.url}?autoplay=1`}
          title={getVideoTitle(video)}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="video-iframe"
        />
      );
    } else {
      return (
        <video
          src={video.url}
          className="video-player"
          controls
          autoPlay
          playsInline
        />
      );
    }
  };

  if (loading) {
    return null;
  }

  if (videos.length === 0) {
    return null;
  }

  return (
    <section className="video-section">
      <div className="video-section-header">
        <h2 className="video-section-title">{sectionText.title}</h2>
        <p className="video-section-subtitle">
          {sectionText.subtitle}
        </p>
      </div>

      <div className={`video-grid video-grid-${videos.length}`}>
        {videos.map((video) => (
          <div key={video.id} className="video-card">
            <div className="video-container">
              {playingVideo === video.id ? (
                renderVideoPlayer(video)
              ) : (
                <div 
                  className="video-thumbnail"
                  onClick={() => handleVideoClick(video.id)}
                >
                  {getThumbnailUrl(video) ? (
                    <img
                      src={getThumbnailUrl(video)}
                      alt={getVideoTitle(video)}
                      className="thumbnail-image"
                      onError={(e) => {
                        if (isYoutubeUrl(video.url)) {
                          e.target.src = `https://img.youtube.com/vi/${extractYoutubeId(video.url)}/hqdefault.jpg`;
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <Play size={48} />
                    </div>
                  )}
                  <div className="play-overlay">
                    <div className="play-button">
                      <Play size={48} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="video-info">
              <h3 className="video-title">{getVideoTitle(video)}</h3>
              {getVideoDescription(video) && (
                <p className="video-description">{getVideoDescription(video)}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default VideoSection;
